import classNames from "classnames";
import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

// See: https://mui.com/material-ui/react-progress/
// See: https://mui.com/material-ui/react-progress/#linear

const ProgressBarColor = {
  primary500: "primary500",
} as const;

const ProgressBarSize = {
  small: "small",
  medium: "medium",
} as const;

const ProgressBarColorConfig = {
  [ProgressBarColor.primary500]: {
    root: "bg-vpBlue-200",
    fill: "bg-vpBlue-500",
  },
} as const;

const ProgressBarSizeConfig = {
  [ProgressBarSize.small]: {
    root: "h-2",
    fill: "h-2",
  },
  [ProgressBarSize.medium]: {
    root: "h-2.5",
    fill: "h-2.5",
  },
} as const;

type ProgressBarRootNode = HTMLDivElement;

interface ProgressBarProps extends React.HTMLAttributes<ProgressBarRootNode> {
  color?: keyof typeof ProgressBarColor;
  max?: number;
  min?: number;
  percent?: number;
  size?: keyof typeof ProgressBarSize;
}

const ProgressBar = forwardRef<ProgressBarRootNode, ProgressBarProps>(
  (
    {
      className,
      color = ProgressBarColor.primary500,
      max = 100,
      min = 0,
      percent = 0,
      size = ProgressBarSize.medium,
      ...props
    },
    ref
  ) => {
    return (
      <div
        aria-valuemax={max}
        aria-valuemin={min}
        aria-valuenow={percent}
        className={twMerge(
          classNames(
            "relative w-full overflow-hidden rounded-full",
            ProgressBarColorConfig[color].root,
            ProgressBarSizeConfig[size].root,
            className
          )
        )}
        role="progressbar"
        ref={ref}
        {...props}
      >
        <div
          className={twMerge(
            classNames("absolute left-0 top-0", ProgressBarColorConfig[color].fill, ProgressBarSizeConfig[size].fill)
          )}
          style={{
            width: `${Math.min(Math.round(percent < min ? min : percent), max)}%`,
          }}
        />
      </div>
    );
  }
);

ProgressBar.displayName = ProgressBar.name;

export default ProgressBar;
